import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import { AuthenticationService, IdleLockService } from '../_services';
import { UserIdentity } from '../helpers/common/user.identity';
import { environment } from '../../environments/environment';
import { MessageService} from 'primeng/api';
import * as Sentry from '@sentry/angular';
import { SILENT_ERROR_HEADER } from './silent-error';
import {IDLE_LOCK_ERROR, expireHeartbeat, isIdleLockError, isReverifyRequiredResponse} from '../auth/models/web-session.util';

@Injectable(
)
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private authenticationService: AuthenticationService,
        private messageService: MessageService,
        private translate: TranslateService,
        public router: Router,
        private idleLock: IdleLockService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Log every outgoing API request as a Sentry breadcrumb.
        // This builds a trail so you can see the sequence of calls leading up to an error.
        let apiPath = request.urlWithParams;
        try { apiPath = new URL(request.urlWithParams).pathname; } catch (e) {}
        Sentry.addBreadcrumb({
            category: 'http',
            message: `${request.method} ${apiPath}`,
            level: 'info',
        });

        // JS-1986: see silent-error.ts.
        const silentError = request.headers.has(SILENT_ERROR_HEADER);
        if (silentError) {
            request = request.clone({headers: request.headers.delete(SILENT_ERROR_HEADER)});
        }

        return next.handle(request)
            .pipe(tap(
                succ => {},
                erro => {
                    // Requests failing after logout (no session cached) — a
                    // teardown straggler, nothing the user should see.
                    if (erro?.status === 401 && !localStorage.getItem('currentUser')) {
                        return;
                    }
                    if (silentError) {
                        return;
                    }
                    const message = erro?.error?.error?.message;
                    if (message) {
                        this.translate.get(message).subscribe(res => {
                            this.messageService.add({
                                severity: 'error',
                                summary: this.translate.instant('Error!'),
                                detail: res
                            });
                        });
                    }
                }),
                catchError((err: HttpErrorResponse) => {
            // JS-1396: the other registration of this interceptor already
            // handled this idle-lock 423 - pass it on untouched, no Sentry.
            if (isIdleLockError(err)) {
                return throwError(IDLE_LOCK_ERROR);
            }
            // Capture non-401 HTTP errors to Sentry with full context
            if (err.status !== 401 && err.status !== 423) {
                const errorMessage = err.error?.error?.message || err.error?.message || err.message || `HTTP ${err.status}`;

                Sentry.withScope(scope => {
                    scope.setTag('http.status_code', String(err.status));
                    scope.setTag('http.url', apiPath);
                    scope.setTag('http.method', request.method);
                    scope.setContext('HTTP Request', {
                        method: request.method,
                        url: request.urlWithParams,
                        api_path: apiPath,
                        status_code: err.status,
                        response_body: err.error,
                    });
                    // Group by endpoint + status so each failing API route is a separate issue
                    scope.setFingerprint(['http-error', request.method, apiPath, String(err.status)]);

                    const sentryError = new Error(`${request.method} ${apiPath} → ${err.status}: ${errorMessage}`);
                    sentryError.name = 'HttpError';
                    Sentry.captureException(sentryError);
                });
            }

            if (err.status === 401) {
                // auto logout if 401 response returned from api — but only
                // when a session is actually cached; post-logout stragglers
                // must not reload the login page from under the user.
                if (localStorage.getItem('currentUser')) {
                    this.authenticationService.logout();
                    location.reload(true);
                }
            }
            if (err.status === 409 && err.error?.code === 'agency_switched') {
                // JS-1802: the account switched agency elsewhere.
                const normalize = (brand) => (brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                if (err.error?.agency_brand && normalize(err.error.agency_brand) !== normalize(environment.agencyName)) {
                    // The new active agency belongs to another whitelabel — this
                    // app can't show it. Log this tab out; logging back in here
                    // re-activates the membership matching this brand.
                    UserIdentity.signOut();
                    location.reload(true);
                } else {
                    // Same brand — resync the cached user and reload so this
                    // session shows the right pool.
                    if (err.error?.user) {
                        UserIdentity.switchAgency(err.error.user);
                    }
                    location.reload(true);
                }
            }
            if (isReverifyRequiredResponse(err)) {
                // JS-1396 idle lock: this device was idle too long (a blob
                // download's 423 counts too, its body is unread). lock() is
                // idempotent. Rethrow the marker (not 'Locked') so a second
                // registration of this interceptor and SentryErrorHandler
                // both recognise it and skip Sentry.
                if (localStorage.getItem('currentUser')) {
                    expireHeartbeat();
                    this.idleLock.lock();
                }
                return throwError(IDLE_LOCK_ERROR);
            }
            if (err.status === 403 && err.error?.error?.message === 'Project does not exist!') {
                this.router.navigate(['']);
            }
            const error = err.error?.error?.message || err.error?.message || err.statusText;
            return throwError(error);
        }))
    }
}
